<script setup lang="ts">
import { ElImageViewer } from 'element-plus';
import 'element-plus/es/components/image-viewer/style/css';
import type { GalleryPhoto } from '~/utils/gallery';

const props = defineProps<{ photos: (GalleryPhoto & { name: string })[]; initialIndex: number; name: string }>();
const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
const viewer = ref<InstanceType<typeof ElImageViewer>>();
const active = ref(props.initialIndex);
const thumbnails = ref<HTMLElement>();
watch(active, async () => {
  await nextTick();
  thumbnails.value?.querySelector<HTMLElement>('[aria-current="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
});
</script>

<template>
  <Teleport to="body">
    <div role="dialog" aria-modal="true" :aria-label="name" class="portfolio-viewer">
      <ElImageViewer ref="viewer" :url-list="photos.map(photo => photo.photo)" :initial-index="initialIndex" :z-index="3000"
        :min-scale="0.5" :max-scale="5" :hide-on-click-modal="true" @close="emit('close')" @switch="active = $event">
        <template #toolbar="{ actions, prev, next, reset }">
          <div class="portfolio-tools">
            <button type="button" :aria-label="t('gallery_module.previous')" @click="prev"><Icon name="lucide:chevron-left" size="22" /></button>
            <button type="button" :aria-label="t('gallery_module.zoom_out')" @click="actions('zoomOut')"><Icon name="lucide:zoom-out" size="22" /></button>
            <button type="button" :aria-label="t('gallery_module.reset')" @click="reset"><Icon name="lucide:maximize" size="22" /></button>
            <button type="button" :aria-label="t('gallery_module.zoom_in')" @click="actions('zoomIn')"><Icon name="lucide:zoom-in" size="22" /></button>
            <button type="button" :aria-label="t('gallery_module.next')" @click="next"><Icon name="lucide:chevron-right" size="22" /></button>
            <button type="button" :aria-label="t('gallery_module.close')" @click="emit('close')"><Icon name="lucide:x" size="22" /></button>
          </div>
        </template>
        <template #viewer-error><p class="portfolio-viewer-error">{{ t('gallery_module.image_error') }}</p></template>
        <div class="portfolio-caption" aria-live="polite">{{ photos[active]?.name }} · {{ active + 1 }} / {{ photos.length }}</div>
        <div ref="thumbnails" class="portfolio-thumbnails" :aria-label="t('gallery_module.thumbnails')">
          <button v-for="(photo, index) in photos" :key="photo.id" type="button" :aria-label="photo.name" :aria-current="index === active ? 'true' : undefined"
            @click="viewer?.setActiveItem(index)">
            <img :src="photo.icon || photo.thumb || photo.photo" alt="" width="64" height="48" loading="lazy" />
          </button>
        </div>
      </ElImageViewer>
    </div>
  </Teleport>
</template>

<style scoped>
.portfolio-viewer { --el-color-primary: var(--el-color-primary-light-3); }
.portfolio-caption { position: absolute; top: 1.2rem; left: 15%; right: 15%; text-align: center; color: #fff; pointer-events: none; }
.portfolio-thumbnails { position: absolute; bottom: 1rem; left: 10%; right: 10%; display: flex; justify-content: safe center; gap: .5rem; overflow-x: auto; padding: .25rem; }
.portfolio-thumbnails button { background: transparent; border: 2px solid transparent; border-radius: .35rem; padding: 0; opacity: .65; cursor: pointer; flex-shrink: 0; }
.portfolio-thumbnails button[aria-current="true"] { border-color: #fff; opacity: 1; }
.portfolio-thumbnails img { display: block; object-fit: cover; border-radius: .2rem; }
.portfolio-viewer :deep(.el-image-viewer__actions) { bottom: 88px; }
.portfolio-viewer :deep(.el-image-viewer__canvas) { padding: 65px 20px 150px; }
.portfolio-tools { display: flex; gap: .5rem; }
.portfolio-tools button { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; padding: 0; border: 0; background: transparent; color: #fff; cursor: pointer; border-radius: .3rem; }
.portfolio-tools button:focus-visible, .portfolio-thumbnails button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
.portfolio-viewer-error { color: #fff; }
@media (max-width: 575px) { .portfolio-thumbnails { left: 1rem; right: 1rem; } .portfolio-tools { gap: .25rem; } }
</style>
